import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import classes from "./CreditCard.module.scss";
import { Button } from "../../../../../../shared/Button/Button";
import { useAppTranslation } from "../../../../../../i18n/useAppTranslation";
import { usePermission } from "../../../../../../hooks/usePermission";
import {
  PERMISSIONS,
  USER_PAYMENT_SYSTEMS,
} from "../../../../../../consts/api";
import { useDeleteCC } from "../../hooks/useDeleteCC";
import { CircularProgress } from "../../../../../../shared/CircularProgress/CircularProgress";
import AddEditCardModal from "../../shared/AddEditCardModal/AddEditCardModal";
import {
  getSetupIntent,
  getClientCardDataStripe,
} from "../../../../../../Actions/Stripe/StripeActions";
import { uiNotification } from "../../../../../../services/UINotificationService";
import Loader from "../../../../../Common/Loader";
import { http } from "../../../../../../services/HttpService";
import { usePatientPaymentMethods } from "../../../../../../hooks/usePatientPaymentMethods";
import { useSetPrimaryPm } from "../../hooks/useSetPrimaryPm";
import { StarIcon } from "../../../../../../assets/Icons/StarIcon";
import { EditIcon } from "../../../../../../assets/Icons/EditIcon";
import { DeleteIcon } from "../../../../../../assets/Icons/DeleteIcon";
import { ConfirmModal } from "../../../../../../boxes/ConfirmModal/ConfirmModal";

const CreditCard = ({
  id,
  cardNumber,
  clientId,
  getSetupIntent,
  getClientCardDataStripe,
  clientClinicId,
  isPrimary,
}) => {
  const { tCommon: t } = useAppTranslation.Common();
  const [confirmPrimaryCC, setConfirmPrimaryCC] = useState(false);
  const [confirmDeleteCC, setConfirmDeleteCC] = useState(false);

  const pms = usePatientPaymentMethods({ patientId: clientId });
  const deleteCC = useDeleteCC({ onSuccess: pms.refetch });
  const setPrimary = useSetPrimaryPm(clientId);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const deleteCcPermission = usePermission(
    PERMISSIONS.patientManagement.deleteCC,
  );

  const openEditModal = () => {
    setIsEditModalOpen(true);
    if (pms.paymentSystem === USER_PAYMENT_SYSTEMS.stripe) {
      getSetupIntent({ patient_id: clientId, clinic_id: clientClinicId });
    }
  };

  const removeStripeCard = async (id) => {
    setShowLoader(true);
    await http
      .post("stripe/detach-payment-method", {
        patient_id: clientId,
        payment_method_id: id,
      })
      .then(() => {
        uiNotification.success("Patient's CC Deleted Successfully");
        pms.refetch();
      })
      .catch((error) => {
        if (error.response) {
          uiNotification.error(error.response.data.message);
        }
      })
      .finally(() => {
        getClientCardDataStripe({ patient_id: clientId });
      });

    setShowLoader(false);
  };

  const isNotUpdatable = () => {
    const notUpdatableMethods = ["klarna", "link"];
    return notUpdatableMethods.includes(cardNumber.toLowerCase());
  };

  const isDeleteCcPermitted =
    deleteCcPermission.loaded && deleteCcPermission.permitted;

  return (
    <div className={classes.root}>
      <div title={cardNumber} className={classes.number}>
        <button
          className={isPrimary ? classes.primaryStar : classes.star}
          onClick={() => {
            if (!isPrimary) {
              setConfirmPrimaryCC(true);
            }
          }}
        >
          <StarIcon width="13px" />
        </button>
        {cardNumber}
      </div>
      {pms.isPosEnabled && (
        <div className={classes.buttons}>
          {!isNotUpdatable() && (
            <Button
              variant="text"
              size="tiny"
              onClick={() => openEditModal()}
              isDisabled={deleteCC.isLoading}
            >
              <EditIcon width="16px" />
            </Button>
          )}
          {isDeleteCcPermitted && (
            <Button
              variant="text"
              size="tiny"
              color="error"
              isDisabled={deleteCC.isLoading}
              onClick={() => {
                setConfirmDeleteCC(true);
              }}
              leftAdornment={
                deleteCC.isLoading ? (
                  <CircularProgress size="tiny" color="red" />
                ) : undefined
              }
            >
              <DeleteIcon width="16px" />
            </Button>
          )}
        </div>
      )}
      {isEditModalOpen && (
        <AddEditCardModal
          cardId={id}
          clientId={clientId}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          removeStripeCard={() => removeStripeCard(id)}
        />
      )}
      {confirmPrimaryCC && (
        <ConfirmModal
          isOpen
          isConfirming={setPrimary.isLoading}
          onClose={() => setConfirmPrimaryCC(false)}
          onCancel={() => setConfirmPrimaryCC(false)}
          onConfirm={() =>
            setPrimary.initiate(id).then(() => {
              setConfirmPrimaryCC(false);
            })
          }
        >
          {t("confirm.setPrimaryCC")}
        </ConfirmModal>
      )}
      {confirmDeleteCC && (
        <ConfirmModal
          isOpen
          isConfirming={deleteCC.isLoading || showLoader}
          onClose={() => setConfirmDeleteCC(false)}
          onCancel={() => setConfirmDeleteCC(false)}
          onConfirm={() => {
            const initiate =
              pms.paymentSystem === USER_PAYMENT_SYSTEMS.stripe
                ? removeStripeCard
                : deleteCC.initiate;

            initiate(id).then(() => {
              setConfirmDeleteCC(false);
            });
          }}
        >
          {t("confirm.deleteCC")}
        </ConfirmModal>
      )}
      <Loader showLoader={showLoader} isFullWidth={true} />
    </div>
  );
};

CreditCard.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  cardNumber: PropTypes.string.isRequired,
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

const mapStateToProps = (state) => {
  return {
    cardList: state.StripeReducer.cardList,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      getSetupIntent,
      getClientCardDataStripe,
    },
    dispatch,
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CreditCard);
